// /business-plan — Internal honest business plan + strategic roadmap.
//
// Positioning: a curated, exclusive Himalayan lifestyle platform.
// Stays + journeys + brand collaborations — not a booking site, not a cottage
// directory. Hosts pay NOTHING. We earn by carrying the brand and curating
// the experiences that belong inside it.

import React from 'react';
import { Link } from 'react-router-dom';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

const Section = ({ children, className = '', testid }) => (
  <section className={`py-16 md:py-20 px-6 md:px-12 ${className}`} data-testid={testid}>
    <div className="max-w-5xl mx-auto">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.3em] text-amber-900/60 mb-3">{children}</div>
);

const H2 = ({ children }) => (
  <h2 className="text-3xl md:text-4xl text-stone-900 font-light italic tracking-tight mb-10" style={pf}>{children}</h2>
);

// ─── Curated-journey programs — the marquee SKUs we sell to luxury / lifestyle brands ───
const TRIP_PROGRAMS = [
  {
    name: 'The Pioneer Run',
    tag: 'Heritage motoring · slow ascent',
    brandFit: 'Royal Enfield · Jawa · Triumph',
    pitch:
      'A 4-day curated motorcycle journey through a Himalayan valley, hosted across hand-picked private homes. Brand supplies the bikes + film crew. We supply the route, the homes, the storytellers, and editorial coverage on GoHimalayan + our channels.',
    revenue: '₹6–9L per activation (8–12 riders × ₹50k + brand sponsorship)',
  },
  {
    name: 'Highline',
    tag: 'Off-road overlanding · luxe basecamps',
    brandFit: 'Land Rover · BMW XM · Mahindra Thar Roxx · Mercedes G',
    pitch:
      'High-altitude 4x4 expedition with two heritage estates as basecamps. Curator-led drive, hot meals at the host\u2019s table, sunrise above the cloud line. Co-branded film + dealership social fodder.',
    revenue: '₹10–15L per activation (brand + 6–10 paying guests)',
  },
  {
    name: 'Long Form',
    tag: 'Creator residencies · slow craft',
    brandFit: 'Apple · Leica · Moleskine · Blinkist · Penguin India',
    pitch:
      'Funded 7-day residencies for writers, photographers, podcasters at a chosen private home. Sponsor covers stay + a stipend in exchange for the work product (a film, an essay, a podcast episode) + credit. Reads like a residency, performs like brand content.',
    revenue: '₹3–4L per slot (4 slots/yr to start)',
  },
  {
    name: 'The Wellness Curve',
    tag: 'Slow recovery · clean food · forest',
    brandFit: 'Kapiva · Forest Essentials · Kama Ayurveda · Tata Tea Premium · Vahdam',
    pitch:
      'A 5-day private wellness retreat at a whole-property cottage or estate. Brand provides product seeding for the cohort + a returning ritual menu. We bring the home, the chef, the forest walks, and the cohort (10–14 women, premium D2C lists).',
    revenue: '₹8–12L per cohort (brand + paying guests)',
  },
  {
    name: 'The Family Trail',
    tag: 'Kids + parents · activities · home cooking',
    brandFit: 'Funskool · Cubelelo · Toehold · Faber-Castell · Decathlon',
    pitch:
      'A 4–5 day programme across the Family Estate Collection. Brand seeds age-appropriate kit (cards, treks, art, optics) and a small content piece. Selfishly drives bookings into our flagship collection.',
    revenue: '₹4–6L per programme (brand + 3–4 families)',
  },
  {
    name: 'Festival of Homes',
    tag: 'Annual flagship · invitation-only · press',
    brandFit: 'Multi-brand · luxury / lifestyle / spirits / mobility',
    pitch:
      'Once a year, a curated weekend across a valley — 30–50 invited guests (founders, editors, creators) hosted across 6–8 properties. Bonfire dinners, an editorial issue, a film. Brands sponsor a home, a meal, a session.',
    revenue: '₹25–40L annually (4–6 sponsor slots × ₹5–8L each)',
  },
];

const BRAND_TARGETS = [
  {
    cat: 'Automotive & Mobility',
    why: 'Mountain drives are the original Indian brand story. Bikes and SUVs perform 10× harder in editorial than in showrooms.',
    names: 'Royal Enfield, Jawa, Triumph, BMW Motorrad, Land Rover, Mahindra, Mercedes G, Toyota Hilux',
  },
  {
    cat: 'Outdoor & Adventure',
    why: 'Our properties double as field offices for the people who already buy these brands.',
    names: 'The North Face, Decathlon, Wildcraft, Quechua, Forclaz, Sequoia, Wildhorn',
  },
  {
    cat: 'Luxury Lifestyle & Wellness',
    why: 'A week at a private estate IS the wellness retreat. We replace the ₹5L-luxury-spa positioning with a ₹50k-bonfire-tea-and-trout positioning. Cleaner, rarer.',
    names: 'Forest Essentials, Kama Ayurveda, Kapiva, Vahdam, Tata Tea Premium, Paper Boat, Soulflower',
  },
  {
    cat: 'Creator & Tools',
    why: 'Our homes already attract photographers and writers. We make it official: residencies, with sponsors.',
    names: 'Apple, Leica, Moleskine, Penguin India, Audible, Blinkist, Adobe',
  },
  {
    cat: 'Spirits & Coffee',
    why: 'A fireplace, a single malt, a 6am pour-over. Native to the format. High-margin to sponsor.',
    names: 'Amrut, Paul John, Indri, Blue Tokai, Sleepy Owl, Third Wave Coffee',
  },
  {
    cat: 'Fashion & Heritage',
    why: 'Shoot ready-to-wear in a wood-stove room with the light hitting the mountain at 4pm. We replace a Mumbai studio with a story.',
    names: 'Nicobar, Forest Essentials, Good Earth, Raw Mango, House of Masaba',
  },
  {
    cat: 'Family & D2C',
    why: 'The Family Estate Collection IS a customer base for D2C kids + home brands. Programmatic, repeatable.',
    names: 'Funskool, Cubelelo, MamaEarth, The Whole Truth, Cosmix, Pee Safe',
  },
];

const REV_STREAMS = [
  {
    name: 'Brand-Funded Curated Journeys',
    desc: 'Named programmes — Pioneer Run, Highline, Long Form, Wellness Curve, Family Trail, Festival of Homes',
    when: 'M3 onward · 4 activations Yr 1 · 12 Yr 2',
    twelve: '₹40–60L',
    logic: 'Editorial fit replaces ad spend with content — high-margin, defensible',
  },
  {
    name: 'Support-Layer Commission on Large Bookings',
    desc: 'Family / whole-property / corporate enquiries routed through us — GoHimalayan curates, takes a commission from the host',
    when: 'Already live (Family Estate Collection)',
    twelve: '₹8–15L',
    logic: '~3 large bookings/mo × ₹50k avg × 15% by M12',
  },
  {
    name: 'Private Offsites',
    desc: '3–5 day buyouts of curated properties for tech / D2C / VC / family-office groups (15–40 ppl)',
    when: 'M4 onward',
    twelve: '₹15–25L',
    logic: '4–6 offsites/yr × avg ₹4L × ~35% margin',
  },
  {
    name: 'State Tourism Technology (B2G)',
    desc: 'White-label inventory + AI curation + booking flow for HP / UK / Sikkim tourism departments',
    when: 'M9 onward (post-credibility milestone)',
    twelve: '₹20–30L',
    logic: '1 MoU @ ₹20L/yr min commit · scale once proven',
  },
  {
    name: 'The Himalayan Store (D2C)',
    desc: 'Artisan tea, textiles, wellness — sold under the GoHimalayan editorial frame',
    when: 'M6 onward',
    twelve: '₹3–5L',
    logic: '~40 orders/mo × avg ₹1.5k × 35% margin',
  },
  {
    name: 'Voluntary Booking Share',
    desc: 'Host opts in to share 5% on platform-driven bookings — never enforced, never required',
    when: 'M5 onward',
    twelve: '₹4–8L',
    logic: '~80 bookings/mo × ₹6k avg × 5% by M12',
  },
];

const COSTS = [
  { name: 'Co-founders (strategy, tech, ops, marketing)', mo: '~₹2L',  notes: 'Lean, founder-led; bench rates' },
  { name: 'Hosting, Mongo, Cloudflare, Emergent', mo: '~₹15k', notes: 'Pipeline is broken into 6 discrete stages — costs trimmed' },
  { name: 'Photographer / videographer field trips', mo: '~₹40k', notes: '1 trip/mo, covers 3–5 properties' },
  { name: 'Instagram ads + creator partnerships', mo: '~₹30k', notes: '5 micro-creators × stay value + paid amplification' },
  { name: 'Brand partnership outreach (decks, films, travel)', mo: '~₹25k', notes: 'The sales engine for the journeys above' },
  { name: 'Tools (Buffer, Notion, Figma, Cleanshot, domain)', mo: '~₹8k', notes: 'Cheap stack' },
];

const MILESTONES = [
  { phase: 'NOW',          headline: 'First 60+ properties live · Family Estate Collection live · structured /apply form · per-stay Launch Kit' },
  { phase: 'Editorial 100',headline: 'Lock the editorial positioning · brand-deck v1 · first two journeys signed (Pioneer Run, Long Form)' },
  { phase: 'Engine ON',    headline: 'Scale acquisition to 250 properties · themed collections (Couples, Pet, Workation, Wellness) · Festival of Homes — flagship event' },
  { phase: 'Government layer', headline: 'First state-tourism MoU · white-label engine · offsite cadence at 1/month' },
  { phase: 'Scale-up',     headline: '500+ properties · Store at scale · multi-brand journey calendar · in-house content studio' },
];

export default function BusinessPlan() {
  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <Section className="bg-stone-900 text-amber-50" testid="bp-hero">
        <Eyebrow>Internal · Strategic Plan</Eyebrow>
        <h1 className="text-4xl md:text-6xl font-light italic tracking-tight leading-[1.05] mb-6" style={pf}>
          An exclusive <span className="text-amber-300">Himalayan lifestyle</span> — curated end-to-end.
        </h1>
        <p className="text-amber-100/80 text-lg max-w-3xl leading-relaxed mb-6">
          GoHimalayan is not a booking platform. It is an editorial guide to a
          rarefied Himalayan way of living — hand-picked private homes,
          curator-led journeys, and the lifestyle brands that earn their place
          inside both. Hosts pay nothing. Ever. We earn by carrying the brand
          and curating the experiences that belong inside it.
        </p>
        <p className="text-amber-100/60 text-sm tracking-wide">
          Internal · revised this week · supersedes the SaaS-tier model entirely.
        </p>
      </Section>

      <Section testid="bp-thesis">
        <Eyebrow>The Thesis</Eyebrow>
        <H2>What we&rsquo;re actually building.</H2>
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <h3 className="text-2xl mb-3 text-stone-900" style={pf}>An editorial commons.</h3>
            <p className="text-stone-700 leading-relaxed">
              Every property we list — homestay, cottage, lodge, estate, villa — is
              hand-picked and host-authored. Free forever. No tier, no listing
              fee, no commission upfront. Hosts keep the relationship with their
              guests; we keep the brand.
            </p>
          </div>
          <div>
            <h3 className="text-2xl mb-3 text-stone-900" style={pf}>A Himalayan lifestyle label.</h3>
            <p className="text-stone-700 leading-relaxed">
              The property is the canvas. Around it we build journeys, films,
              residencies, festivals, and a small store. We don&rsquo;t compete
              with OTAs — we sell the <em>idea</em> of a slow, exclusive
              Himalayan life, and we earn from the brands that want to belong
              inside it.
            </p>
          </div>
        </div>
      </Section>

      <Section className="bg-amber-50/60" testid="bp-commons">
        <Eyebrow>For the host</Eyebrow>
        <H2>The four promises we never break.</H2>
        <div className="grid sm:grid-cols-2 gap-6">
          {[
            ['Free, forever.',          'No listing fee. No SaaS tier. No upgrade unlock. The host keeps every rupee from every booking.'],
            ['Direct WhatsApp.',         'Guest enquiries route to the host\u2019s phone. We never sit in between unless we are adding real value (curation, large bookings).'],
            ['Editorial care.',          'A real curator writes the story, picks the photos, polishes the listing. Hosts receive a launch kit to broadcast.'],
            ['No platform middle-man.',  'Reviews live with the host. The relationship lives with the host. We are the discovery layer, not the toll booth.'],
          ].map(([t, d]) => (
            <div key={t} className="bg-white rounded-2xl p-6 border border-amber-900/10">
              <h3 className="text-xl mb-2 text-stone-900" style={pf}>{t}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section testid="bp-trips">
        <Eyebrow>Marquee programmes</Eyebrow>
        <H2>Curated journeys — the named programmes we sell to lifestyle brands.</H2>
        <p className="text-stone-600 mb-10 max-w-3xl leading-relaxed">
          Each programme is a turnkey, brand-friendly format. The properties
          are the set. The journey is the script. The brand belongs inside the
          story without ever shouting. We sell exclusivity and craft — not
          banner ads.
        </p>
        <div className="space-y-4">
          {TRIP_PROGRAMS.map((p) => (
            <div key={p.name} className="bg-white rounded-2xl p-6 md:p-8 border border-amber-900/10 hover:shadow-lg transition-shadow">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                <h3 className="text-2xl text-stone-900" style={pf}>{p.name}</h3>
                <span className="text-xs uppercase tracking-[0.25em] text-amber-800">{p.tag}</span>
              </div>
              <p className="text-[11px] tracking-[0.18em] uppercase text-stone-500 mb-3">Brand fit · {p.brandFit}</p>
              <p className="text-stone-700 leading-relaxed mb-4">{p.pitch}</p>
              <p className="text-xs text-emerald-800 font-semibold">{p.revenue}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-stone-100" testid="bp-brands">
        <Eyebrow>The brand engine</Eyebrow>
        <H2>Who we sell to — and why they buy.</H2>
        <p className="text-stone-600 mb-10 max-w-3xl leading-relaxed">
          We aren&rsquo;t after the biggest brands. We&rsquo;re after the right
          ones — whose customers already aspire to the kind of week our
          properties make possible. Editorial fit first, scale second.
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          {BRAND_TARGETS.map((b) => (
            <div key={b.cat} className="bg-white rounded-2xl p-6 border border-stone-200">
              <h3 className="text-xl text-stone-900 mb-2" style={pf}>{b.cat}</h3>
              <p className="text-sm text-stone-600 italic mb-3 leading-relaxed">{b.why}</p>
              <p className="text-xs text-amber-900/80 leading-relaxed"><b>On the list:</b> {b.names}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section testid="bp-revenue">
        <Eyebrow>The Money</Eyebrow>
        <H2>Revenue — no host pays us, brands do.</H2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-[0.2em] text-stone-500 border-b border-stone-200">
              <tr>
                <th className="py-3 pr-4">Stream</th>
                <th className="py-3 pr-4">When it lights up</th>
                <th className="py-3 pr-4">Yr-1 Range</th>
              </tr>
            </thead>
            <tbody>
              {REV_STREAMS.map((r) => (
                <tr key={r.name} className="border-b border-stone-100 align-top">
                  <td className="py-4 pr-4">
                    <div className="font-semibold text-stone-900">{r.name}</div>
                    <div className="text-xs text-stone-500 mt-1 leading-snug">{r.desc}</div>
                    <div className="text-[11px] text-amber-800/70 mt-1 italic">{r.logic}</div>
                  </td>
                  <td className="py-4 pr-4 text-stone-600">{r.when}</td>
                  <td className="py-4 pr-4 font-semibold text-emerald-800">{r.twelve}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-6 text-sm text-stone-500 italic">
          Indicative ranges, founder-honest. Real numbers depend on which 2–3
          journey programmes land first; one signed Pioneer Run or Highline
          pays for half the operating year.
        </p>
      </Section>

      <Section className="bg-amber-50/60" testid="bp-costs">
        <Eyebrow>Cost base</Eyebrow>
        <H2>What it costs to keep this running.</H2>
        <div className="space-y-3">
          {COSTS.map((c) => (
            <div key={c.name} className="bg-white rounded-xl p-4 flex flex-wrap items-baseline gap-x-6 gap-y-1 border border-amber-900/10">
              <div className="flex-1 font-semibold text-stone-900 min-w-[260px]">{c.name}</div>
              <div className="text-emerald-800 font-mono text-sm">{c.mo}/mo</div>
              <div className="text-xs text-stone-500 flex-1 min-w-[200px]">{c.notes}</div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-stone-700">
          Total burn ~<b>₹3.2L/month</b>, ~<b>₹38L/yr</b>. Two journey
          activations + one State MoU clears the year.
        </p>
      </Section>

      <Section testid="bp-milestones">
        <Eyebrow>Internal milestones</Eyebrow>
        <H2>What unlocks what — without dates.</H2>
        <div className="relative pl-8 border-l-2 border-amber-300 space-y-8">
          {MILESTONES.map((m, i) => (
            <div key={m.phase} className="relative">
              <span className="absolute -left-[2.4rem] top-1 w-5 h-5 rounded-full bg-amber-400 border-4 border-[#FAF7F0]" />
              <div className="text-[11px] uppercase tracking-[0.3em] text-amber-900/70 mb-1">{`Phase ${i+1} · ${m.phase}`}</div>
              <p className="text-stone-800 leading-relaxed">{m.headline}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-stone-900 text-amber-50" testid="bp-links">
        <Eyebrow>Where this connects</Eyebrow>
        <div className="grid md:grid-cols-3 gap-6">
          <Link to="/about" className="bg-stone-800 rounded-2xl p-6 hover:bg-stone-700 transition-colors block">
            <h3 className="text-xl mb-2 text-amber-200" style={pf}>About</h3>
            <p className="text-amber-100/70 text-sm leading-relaxed">The public-facing version of the same thesis — what we tell hosts and guests.</p>
          </Link>
          <Link to="/admin/collections" className="bg-stone-800 rounded-2xl p-6 hover:bg-stone-700 transition-colors block">
            <h3 className="text-xl mb-2 text-amber-200" style={pf}>Collections</h3>
            <p className="text-amber-100/70 text-sm leading-relaxed">Where Family · Couples · Pet · Workation become themed bundles for the journeys above.</p>
          </Link>
          <Link to="/admin/launch-kit" className="bg-stone-800 rounded-2xl p-6 hover:bg-stone-700 transition-colors block">
            <h3 className="text-xl mb-2 text-amber-200" style={pf}>Launch Kit</h3>
            <p className="text-amber-100/70 text-sm leading-relaxed">Per-stay broadcast pack we hand each newly-listed host so they can re-share their feature.</p>
          </Link>
        </div>
      </Section>
    </div>
  );
}
